import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/content";
import LeadForm from "@/components/LeadForm";
import FAQAccordion from "@/components/FAQAccordion";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import {
  articleSchema,
  breadcrumbSchema,
  faqPageSchema,
  localBusinessSchema,
  SITE_URL,
} from "@/lib/schema";

const PRODUCT_LINKS = {
  steno: { url: "https://stenopacemaster.com", label: "stenopacemaster.com", color: "#1E3A8A" },
  draftmaster: { url: "https://draftmaster.ai", label: "draftmaster.ai", color: "#065F46" },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useSEO({
    title: post
      ? `${post.title} | LexWorkspace Insights`
      : "Insights | LexWorkspace",
    description: post ? post.excerpt : undefined,
    keywords: post ? (post.keywords || []).join(", ") : undefined,
    image: post ? post.image : undefined,
    url: post ? SITE_URL + "/blog/" + post.slug : SITE_URL + "/blog",
    type: "article",
  });

  if (!post) return <Navigate to="/blog" replace />;

  const link = PRODUCT_LINKS[post.product];
  const related = BLOG_POSTS.filter(
    (p) => p.product === post.product && p.slug !== post.slug
  ).slice(0, 2);

  // Collect FAQ blocks for AEO schema
  const faqBlocks = (post.body || []).filter(
    (b) => typeof b === "object" && b && b.type === "faq"
  );
  const faqItems = faqBlocks.flatMap((b) => b.items || []);

  return (
    <article data-testid={`blog-post-${post.slug}`}>
      <JsonLd
        id={`article-${post.slug}`}
        data={articleSchema({
          title: post.title,
          description: post.excerpt,
          image: post.image,
          url: SITE_URL + "/blog/" + post.slug,
          product: post.product,
          datePublished: post.datePublished || "2026-01-01",
        })}
      />
      {faqItems.length > 0 && (
        <JsonLd
          id={`article-${post.slug}-faq`}
          data={faqPageSchema(faqItems)}
        />
      )}
      {post.localBusiness && (
        <JsonLd
          id={`article-${post.slug}-local`}
          data={localBusinessSchema({
            city: post.localBusiness.city,
            state: post.localBusiness.state,
            url: SITE_URL + "/blog/" + post.slug,
            product: post.product,
            productUrl:
              post.product === "steno"
                ? "https://stenopacemaster.com"
                : "https://draftmaster.ai",
          })}
        />
      )}
      <JsonLd
        id={`article-${post.slug}-breadcrumbs`}
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Insights", url: SITE_URL + "/blog" },
          { name: post.title, url: SITE_URL + "/blog/" + post.slug },
        ])}
      />
      <header className="border-b border-black/10">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-24 lg:py-32">
          <Link
            to="/blog"
            className="text-xs tracking-[0.25em] uppercase font-semibold text-gray-500 hover:text-black"
          >
            ← Insights
          </Link>
          <p
            className="text-xs tracking-[0.25em] uppercase font-semibold mt-8 mb-6"
            style={{ color: link.color }}
          >
            {post.product === "steno" ? "Stenography" : "Legal AI"}
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {post.title}
          </h1>
          <p className="text-gray-600 mt-6 text-lg leading-relaxed">
            {post.excerpt}
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <div className="aspect-[16/9] overflow-hidden mb-12 border border-black/10">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="prose-content space-y-6">
          {post.body.map((block, i) => {
            // Legacy: plain string paragraph
            if (typeof block === "string") {
              return (
                <p
                  key={i}
                  className="text-base text-gray-800 leading-relaxed font-light"
                >
                  {block}
                </p>
              );
            }
            if (!block) return null;
            if (block.type === "h2") {
              return (
                <h2
                  key={i}
                  className="font-serif-display text-3xl sm:text-4xl mt-12 mb-2 leading-tight"
                >
                  {block.text}
                </h2>
              );
            }
            if (block.type === "p") {
              return (
                <p
                  key={i}
                  className="text-base text-gray-800 leading-relaxed font-light"
                >
                  {block.text}
                </p>
              );
            }
            if (block.type === "ul") {
              return (
                <ul
                  key={i}
                  className="list-disc pl-6 space-y-2 text-base text-gray-800 leading-relaxed font-light"
                >
                  {block.items.map((it, j) => (
                    <li key={j}>{it}</li>
                  ))}
                </ul>
              );
            }
            if (block.type === "callout") {
              return (
                <div
                  key={i}
                  className="my-4 p-6 border-l-4 bg-gray-50"
                  style={{ borderColor: link.color }}
                >
                  <p className="text-sm font-semibold uppercase tracking-widest mb-2" style={{ color: link.color }}>
                    {block.label || "Note"}
                  </p>
                  <p className="text-base text-gray-800 leading-relaxed">
                    {block.text}
                  </p>
                  {block.cta && block.url && (
                    <a
                      href={block.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-sm font-semibold tracking-wide uppercase"
                      style={{ color: link.color }}
                    >
                      {block.cta} <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
              );
            }
            if (block.type === "faq") {
              return (
                <div key={i} className="mt-12">
                  <FAQAccordion
                    items={block.items}
                    testIdPrefix={`post-${post.slug}-faq`}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>

        <div
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-0 border border-black/10"
          data-testid="post-dual-promotion"
        >
          {/* PRIMARY product (post.product) */}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="post-outbound-primary"
            className="group p-8 lg:p-10 border-b md:border-b-0 md:border-r border-black/10 hover:bg-[#FAFAFA] transition-colors"
            style={{ borderLeftWidth: 4, borderLeftColor: link.color }}
          >
            <p
              className="text-xs tracking-[0.25em] uppercase font-semibold mb-3"
              style={{ color: link.color }}
            >
              {post.product === "steno"
                ? "For court reporters & captioners"
                : "For attorneys & legal teams"}
            </p>
            <p className="font-serif-display text-2xl mb-4 leading-tight">
              {post.product === "steno"
                ? "Practice these techniques on StenoPaceMaster."
                : "Put DraftMaster.ai to work this week."}
            </p>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {post.product === "steno"
                ? "The structured online stenography speed-building program — RPR/RMR/CRR and state CSR certification tracks, graduated dictation, realtime accuracy drills."
                : "AI legal drafting and contract automation for attorneys, paralegals and corporate legal teams — clause-library control, citation safety, per-clause audit trails."}
            </p>
            <span
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all"
              style={{ color: link.color }}
            >
              Visit {link.label} <ArrowUpRight size={14} />
            </span>
          </a>

          {/* CROSS-product (the other one) */}
          {(() => {
            const cross =
              post.product === "steno"
                ? PRODUCT_LINKS.draftmaster
                : PRODUCT_LINKS.steno;
            const crossIsSteno = post.product !== "steno";
            return (
              <a
                href={cross.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="post-outbound-cross"
                className="group p-8 lg:p-10 hover:bg-[#FAFAFA] transition-colors"
                style={{ borderLeftWidth: 4, borderLeftColor: cross.color }}
              >
                <p
                  className="text-xs tracking-[0.25em] uppercase font-semibold mb-3"
                  style={{ color: cross.color }}
                >
                  {crossIsSteno
                    ? "Also serves court reporters"
                    : "Also serves law firms"}
                </p>
                <p className="font-serif-display text-2xl mb-4 leading-tight">
                  {crossIsSteno
                    ? "Build steno speed on StenoPaceMaster."
                    : "Draft faster on DraftMaster.ai."}
                </p>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  {crossIsSteno
                    ? "Cleaner realtime transcripts upstream produce cleaner AI-assisted briefs downstream. Train to 96%+ certification accuracy with StenoPaceMaster."
                    : "Reporters who recommend AI legal drafting tools to their firm clients become indispensable. DraftMaster.ai is the platform built for legal work."}
                </p>
                <span
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold group-hover:gap-3 transition-all"
                  style={{ color: cross.color }}
                >
                  Visit {cross.label} <ArrowUpRight size={14} />
                </span>
              </a>
            );
          })()}
        </div>

        {/* Keywords */}
        <div className="mt-12 flex flex-wrap gap-2">
          {post.keywords.map((k) => (
            <span
              key={k}
              className="text-xs px-3 py-1.5 border border-black/15 text-gray-600"
            >
              {k}
            </span>
          ))}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-24 border-t border-black/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <h2 className="font-serif-display text-3xl mb-12">
              Keep reading.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group border border-black/10 bg-white hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {p.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LEAD */}
      <section className="bg-[#111] text-white py-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 grid gap-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-3">
              Subscribe
            </p>
            <h2 className="font-serif-display text-4xl leading-none">
              One short email a month. No spam.
            </h2>
          </div>
          <LeadForm
            interest={post.product}
            source={`blog-${post.slug}`}
            variant="dark"
            testIdPrefix="post-lead"
            ctaLabel="Subscribe"
          />
        </div>
      </section>
    </article>
  );
}
